import React, { useState, useEffect, useContext } from "react"
import { Dropdown, DropdownToggle, DropdownMenu, Button, Input, Label, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from "reactstrap"
import gql from "graphql-tag"
import { useQuery } from "@apollo/client"
import moment from "moment"
import Select from "react-select"
import withApolloClient from "../../withApolloClient"

import ConfirmationPopup from "../../Material/ConfirmationPopup"
import { GroupsTypeAhead, RepsTypeAhead } from "./TypeAheadComponents"
import { BoardQuery } from "../Queries"
import "./BoardActionsMenu.css"
import { CurrentUserContext } from "../../Context/CurrentUserContext"

const UpdateBoardMutation = gql`
  mutation UpdateBoardMutation($input: UpdateBoardMutationInput!) {
    updateBoard(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const boardTypeOptions = [
    { value: true, label: "Private" },
    { value: false, label: "Shared" },
]

const BoardActionsMenu = ({
    board = null,
    canChangeBoard = false,
    canDeleteBoard = false,
    onArchiveStatusChange,
    apolloClient,
}) => {
    const { currentUser } = useContext(CurrentUserContext)
    // Identity and archived-ness come off the board the sidebar already holds; the fetched
    // board below only ever adds to it, so neither is worth passing separately.
    const boardId = board?.id
    const isArchived = !!board?.isArchived
    const [open, setOpen] = useState(false)
    // The menu is rendered once per sidebar row, so anything fetched on mount is fetched
    // once per board. Nothing this component fetches is on screen until the menu is opened,
    // so the fetch waits for that -- see the `board` prop below for what it covers instead.
    const [menuRequested, setMenuRequested] = useState(false)
    const [newTitle, setNewTitle] = useState("")
    const [description, setDescription] = useState("")
    const [isPrivate, setIsPrivate] = useState(true)
    const [roundRobinEnabled, setRoundRobinEnabled] = useState(false)
    const [selectedUsers, setSelectedUsers] = useState([])
    const [selectedGroups, setSelectedGroups] = useState([])
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(false)
    const [openModal, setOpenModal] = useState("")

    // AllBoardsQuery already hands the sidebar every field the details form shows, so `board`
    // covers this component until the share lists are needed. BoardQuery is fetched only for
    // sharedWithGroups/sharedWithUsers, and only once the row's menu has been opened.
    // cache-first, not cache-and-network: updateBoard refetches "Board" itself, so a reopen
    // has nothing new to learn and must not reseed the form under an open modal.
    const { data } = useQuery(BoardQuery, {
        variables: { id: boardId },
        fetchPolicy: "cache-first",
        skip: !boardId || !menuRequested,
    })

    const boarddata = data?.board || board
    // Seeded off the values rather than off `boarddata` itself: the sidebar re-renders on any
    // board change and hands down a fresh `board` object each time, and an effect keyed on
    // that reference would reset the form under whoever is typing in it.
    const { title: boardTitle, description: boardDescription, isPrivate: boardIsPrivate, roundRobinEnabled: boardRoundRobinEnabled } = boarddata || {}
    // Only ever present on the fetched board, and stable while it sits in the cache.
    const sharedWithGroups = data?.board?.sharedWithGroups
    const sharedWithUsers = data?.board?.sharedWithUsers

    useEffect(() => {
        if (!boardTitle && !boardDescription && boardIsPrivate === undefined) return

        setNewTitle(boardTitle || "")
        setDescription(boardDescription || "")
        setIsPrivate(boardIsPrivate)
        setRoundRobinEnabled(!!boardRoundRobinEnabled)
    }, [boardTitle, boardDescription, boardIsPrivate, boardRoundRobinEnabled])

    useEffect(() => {
        if (!sharedWithGroups && !sharedWithUsers) return

        setSelectedGroups(
            sharedWithGroups?.edges?.map((edge) => ({
                value: edge.node.id,
                label: edge.node.name,
            })) || []
        )
        setSelectedUsers(sharedWithUsers?.edges?.map((edge) => edge.node) || [])
    }, [sharedWithGroups, sharedWithUsers])

    const updateBoard = async (values = {}) => {
        // Keep archived boards read-only while allowing them to be unarchived.
        if (isArchived && !(Object.keys(values).length === 1 && "isArchived" in values)) return false

        setLoading(true)
        setError("")
        try {
            const response = await apolloClient.mutate({
                mutation: UpdateBoardMutation,
                variables: { input: { boardId, ...values } },
                refetchQueries: ["Board", "AllBoards"],
            })
            if (!response?.data?.updateBoard?.ok) {
                setError(response?.data?.updateBoard?.errors?.[0]?.messages?.[0] || "Update failed")
                return false
            }
            return true
        } catch (err) {
            setError(err.message || "Update failed")
            return false
        } finally {
            setLoading(false)
        }
    }

    const saveDetails = async () => {
        if (!newTitle.trim()) return

        const ok = await updateBoard({
            title: newTitle.trim(),
            description: description?.trim() || "",
            isPrivate,
            roundRobinEnabled,
        })
        if (ok) {
            setOpenModal("")
            setOpen(false)
        }
    }

    const savePermissions = async () => {
        const ok = await updateBoard({
            sharedWithUserIds: selectedUsers.map((user) => user.id),
            sharedWithGroupIds: selectedGroups.map((group) => group.value),
        })
        if (ok) {
            setOpenModal("")
            setOpen(false)
        }
    }

    const handleArchiveStatusChange = async () => {
        const ok = await updateBoard({ isArchived: !isArchived })
        if (ok) {
            setOpenModal("")
            setOpen(false)
            await onArchiveStatusChange?.({
                boardId,
                isArchived: !isArchived,
            })
        }
    }

    const boardOwnerName = boarddata?.owner
        ? (boarddata.owner.firstName ? `${boarddata.owner.firstName} ${boarddata.owner.lastName || ""}`.trim() : boarddata.owner.username)
        : "--"
    const isOwner = !!(boarddata?.owner?.id && currentUser?.id && boarddata.owner.id === currentUser.id)

    if (!boardId || !canChangeBoard) return null

    return (
        <>
            <Dropdown isOpen={open} toggle={() => { setMenuRequested(true); setOpen(!open) }} direction="down">
                <DropdownToggle tag="span" data-testid="board-actions-toggle" className="menu-trigger">
                    <i className="fa fa-ellipsis-h fa-2x" />
                </DropdownToggle>
                <DropdownMenu className="board-action-main-menu" container="body" strategy="fixed">
                    {!isArchived && (
                        <>
                            <div className="menu-item" onClick={() => { setError(""); setOpenModal("details"); setOpen(false) }}>
                                <i className="fa fa-pencil-square-o" /> Board Details
                            </div>
                            <div className="menu-item" onClick={() => { setError(""); setOpenModal("permissions"); setOpen(false) }}>
                                <i className="fa fa-shield" /> Manage Permissions
                            </div>
                        </>
                    )}
                    {canDeleteBoard && (
                        <>
                            {!isArchived && <hr className="m-0" />}
                            <div className={`menu-item ${isArchived ? "text-success" : "text-danger"}`} onClick={() => setOpenModal("archiveBoard")}>
                                <i className={isArchived ? "fa fa-undo text-success" : "fa fa-archive text-danger"} /> {isArchived ? " Unarchive" : " Archive"}
                            </div>
                        </>
                    )}
                </DropdownMenu>
            </Dropdown>

            <Modal isOpen={openModal === "details"} toggle={() => !loading && setOpenModal("")} className="board-details-modal">
                <ModalHeader>Board Details</ModalHeader>
                <ModalBody>
                    <div className="board-details-card">
                        <div className="board-details-meta">
                            <div className="board-details-meta-item">
                                <span className="board-details-meta-label">Created By</span>
                                <span className="board-details-meta-value">{boardOwnerName}</span>
                            </div>
                            <div className="board-details-meta-item">
                                <span className="board-details-meta-label">Date Added</span>
                                <span className="board-details-meta-value">{boarddata?.dateAdded ? moment(boarddata.dateAdded).format("MMM D, YYYY h:mm A") : "--"}</span>
                            </div>
                            <div className="board-details-meta-item">
                                <span className="board-details-meta-label">Board ID</span>
                                <span className="board-details-meta-value">{boarddata?.pk || "--"}</span>
                            </div>
                        </div>

                        <div className="board-details-primary-fields">
                            <FormGroup>
                                <Label className="board-details-label">Board Name</Label>
                                <Input
                                    value={newTitle}
                                    onChange={(e) => setNewTitle(e.target.value)}
                                    placeholder="Enter board name"
                                    autoFocus
                                    disabled={!isOwner}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label className="board-details-label">Board Type</Label>
                                <Select
                                    className="bos-custom-select"
                                    classNamePrefix="bos-select"
                                    options={boardTypeOptions}
                                    value={boardTypeOptions.find((option) => option.value === isPrivate)}
                                    onChange={(selected) => setIsPrivate(selected.value)}
                                    isDisabled={!isOwner}
                                    isSearchable={false}
                                />
                            </FormGroup>
                        </div>

                        <FormGroup>
                            <Label className="board-details-label">Round Robin Assignment</Label>
                            <div>
                                <span>On</span>
                                <div className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        className="toggle-switch-checkbox"
                                        name="roundRobinEnabled"
                                        id="boardRoundRobinEnabled"
                                        checked={!roundRobinEnabled}
                                        disabled={!isOwner}
                                        onChange={() => setRoundRobinEnabled(!roundRobinEnabled)}
                                    />
                                    <label className="toggle-switch-label" htmlFor="boardRoundRobinEnabled">
                                        <span className="toggle-switch-inner" />
                                        <span className="toggle-switch-switch" />
                                    </label>
                                </div>
                                <span>Off</span>
                            </div>
                        </FormGroup>

                        <FormGroup className="mb-0">
                            <Label className="board-details-label">Description</Label>
                            <Input
                                type="textarea"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Add a short description for this board"
                                rows="5"
                                disabled={!isOwner}
                            />
                        </FormGroup>

                        {!isOwner && <Alert color="info" className="mb-0 mt-3">Only the board owner can edit the board details.</Alert>}
                        {error && <Alert color="danger" className="mb-0 mt-3">{error}</Alert>}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" onClick={() => setOpenModal("")} disabled={loading}>
                        Cancel
                    </Button>
                    <Button color="primary" type="button" onClick={saveDetails} disabled={loading || !isOwner}>
                        {loading ? "Saving..." : "Save Changes"}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={openModal === "permissions"} toggle={() => !loading && setOpenModal("")} className="board-permissions-modal">
                <ModalHeader>Manage Board Permissions</ModalHeader>
                <ModalBody>
                    <div className="board-permissions-section">
                        <Label className="board-permissions-label">Shared With Groups</Label>
                        <p className="board-permissions-help">Select the groups that should be able to access this board.</p>
                        <GroupsTypeAhead defaultValue={selectedGroups} onChange={setSelectedGroups} returnOptions />
                    </div>
                    <div className="board-permissions-section">
                        <Label className="board-permissions-label">Shared With Users</Label>
                        <p className="board-permissions-help">Select individual users who should also have access to this board.</p>
                        <RepsTypeAhead defaultValue={selectedUsers} onChange={setSelectedUsers} returnObjects />
                    </div>
                    {error && <Alert color="danger" className="mb-0">{error}</Alert>}
                </ModalBody>
                <ModalFooter>
                    <Button type="button" onClick={() => setOpenModal("")} disabled={loading}>
                        Cancel
                    </Button>
                    <Button color="primary" type="button" onClick={savePermissions} disabled={loading}>
                        {loading ? "Saving..." : "Save Permissions"}
                    </Button>
                </ModalFooter>
            </Modal>

            <ConfirmationPopup
                open={openModal === "archiveBoard"}
                message={`Are you sure you want to ${isArchived ? "unarchive" : "archive"} this board?`}
                loading={loading}
                handleClose={() => setOpenModal("")}
                action={"Yes"}
                confirmAction={handleArchiveStatusChange}
                color={isArchived ? "success" : "danger"}
                error={error}
            />
        </>
    )
}

export default withApolloClient(BoardActionsMenu)
